import { useEffect, useRef, useState, type RefObject } from "react";

interface Options {
  threshold?: number;
  resistance?: number;
  enabled?: boolean;
}

export function usePullToRefresh(
  scrollRef: RefObject<HTMLElement | null>,
  onRefresh: () => void,
  options: Options = {}
) {
  const { threshold = 80, resistance = 2.5, enabled = true } = options;
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startYRef = useRef<number | null>(null);
  const startXRef = useRef<number | null>(null);
  const activeRef = useRef<boolean>(false);
  const pullDistanceRef = useRef<number>(0);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;
    if (!("ontouchstart" in window)) return;

    const el = scrollRef.current;
    if (!el) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (el.scrollTop > 0) {
        startYRef.current = null;
        startXRef.current = null;
        return;
      }
      const touch = e.touches[0];
      startYRef.current = touch.clientY;
      startXRef.current = touch.clientX;
      activeRef.current = false;
      pullDistanceRef.current = 0;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (startYRef.current === null || startXRef.current === null) return;

      const touch = e.touches[0];
      const deltaY = touch.clientY - startYRef.current;
      const deltaX = touch.clientX - startXRef.current;

      if (el.scrollTop > 0) {
        startYRef.current = null;
        startXRef.current = null;
        activeRef.current = false;
        pullDistanceRef.current = 0;
        setPullDistance(0);
        return;
      }

      if (deltaY <= 0) {
        if (activeRef.current) {
          activeRef.current = false;
          pullDistanceRef.current = 0;
          setPullDistance(0);
        }
        return;
      }

      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        startYRef.current = null;
        startXRef.current = null;
        activeRef.current = false;
        pullDistanceRef.current = 0;
        setPullDistance(0);
        return;
      }

      activeRef.current = true;
      const next = Math.min(deltaY / resistance, threshold * 1.5);
      pullDistanceRef.current = next;
      setPullDistance(next);
      if (e.cancelable) e.preventDefault();
    };

    const handleTouchEnd = () => {
      if (activeRef.current && pullDistanceRef.current >= threshold) {
        setIsRefreshing(true);
        onRefresh();
      }
      startYRef.current = null;
      startXRef.current = null;
      activeRef.current = false;
      pullDistanceRef.current = 0;
      setPullDistance(0);
    };

    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd);
    el.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
      el.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [scrollRef, onRefresh, threshold, resistance, enabled]);

  return { pullDistance, isRefreshing };
}
